import React, { useState } from 'react';
import type { FieldErrors, Qualification } from '../../types/onboarding';
import { RatingField, TextAreaField, YesNoField } from './OnboardingField';
import { GoldButton, OnboardingProgress, StepHeader, TnsWordmark } from './OnboardingShell';
interface StepQualificationProps {
  value: Qualification;
  onChange: (value: Qualification) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
}
export const validateQualification = (q: Qualification): FieldErrors => {
  const errors: FieldErrors = {};
  if (!q.q1HelpedSomeone.trim()) errors.q1HelpedSomeone = 'Share one specific moment — it matters here.';
  if (!q.q2ShapedCulture.trim()) errors.q2ShapedCulture = 'Tell us where you’ve left a mark.';
  if (!q.q3BuildingNow.trim()) errors.q3BuildingNow = 'We’d like to know what you’re building.';
  if (!q.q4BringToRoom.trim()) errors.q4BringToRoom = 'Let us know what you’d bring.';
  if (q.consistencyRating === null) errors.consistencyRating = 'Pick the number that feels honest.';
  if (!q.financialCommitment) errors.financialCommitment = 'Let us know either way.';
  return errors;
};
export const StepQualification = ({
  value,
  onChange,
  onBack,
  onSubmit,
  submitting
}: StepQualificationProps) => {
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = <K extends keyof Qualification,>(key: K, next: Qualification[K]) => {
    onChange({
      ...value,
      [key]: next
    });
    if (errors[key as string]) {
      setErrors(prev => {
        const copy = {
          ...prev
        };
        delete copy[key as string];
        return copy;
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateQualification(value);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit();
  };
  return <>
      <TnsWordmark className="mb-10 sm:mb-14" data-id="element-927" data-mp="26t0hytq948hb" />
      <OnboardingProgress step={2} data-id="element-928" data-mp="22qncm6ff2cl2" />
      <StepHeader heading="Now tell us who you are when no one’s watching." subtext="We are not asking if you’re willing to help others. Everyone says yes to that. We are asking what you have already done." data-id="element-929" data-mp="22qduf50m59px" />

      <form onSubmit={handleSubmit} noValidate className="space-y-8" data-id="element-930" data-mp="1j429w7kakc06">
        <TextAreaField id="q1HelpedSomeone" label="Tell us about a specific time you used your resources, skill, network, or money to help someone else advance their business, career, idea, or agenda. What happened as a result?" value={value.q1HelpedSomeone} onChange={v => set('q1HelpedSomeone', v)} error={errors.q1HelpedSomeone} rows={6} data-id="element-931" data-mp="1j5vk1wu45ecx" />
        <TextAreaField id="q2ShapedCulture" label="Where have you shaped culture in a community, workplace, or industry? What changed because you were there?" value={value.q2ShapedCulture} onChange={v => set('q2ShapedCulture', v)} error={errors.q2ShapedCulture} rows={5} data-id="element-932" data-mp="220tzeyqffnco" />
        <TextAreaField id="q3BuildingNow" label="What are you currently building or growing for yourself?" value={value.q3BuildingNow} onChange={v => set('q3BuildingNow', v)} error={errors.q3BuildingNow} rows={5} data-id="element-933" data-mp="38vsug749b4xs" />
        <TextAreaField id="q4BringToRoom" label="Beyond your presence, what would you personally bring into this room?" value={value.q4BringToRoom} onChange={v => set('q4BringToRoom', v)} error={errors.q4BringToRoom} rows={5} data-id="element-934" data-mp="3dn2l9b1jkeb9" />
        <RatingField id="consistencyRating" label="How would you rate your own consistency in showing up for others, not just yourself, over the last year?" value={value.consistencyRating} onChange={v => set('consistencyRating', v)} error={errors.consistencyRating} data-id="element-935" data-mp="2zwpdb8ongzlx" />
        <YesNoField id="financialCommitment" label="Membership means investing between ₦30,000 and ₦100,000 a year to access Networking Salons, Masterclasses, podcast sessions, cocktail mixers, and other community experiences. Are you able and willing to make this investment?" value={value.financialCommitment} onChange={v => set('financialCommitment', v)} error={errors.financialCommitment} data-id="element-936" data-mp="a62hunil4rtu" />

        <div className="pt-4 flex flex-col sm:flex-row-reverse sm:items-center sm:justify-start gap-4 sm:gap-6" data-id="element-937" data-mp="2yua192xmiiz3">
          <GoldButton type="submit" disabled={submitting} data-id="element-938" data-mp="33sjv9a7m3dj4">
            {submitting ? 'Submitting…' : 'Submit Application'}
          </GoldButton>
          <button type="button" onClick={onBack} disabled={submitting} className="font-body text-tns-charcoal-soft hover:text-tns-navy underline underline-offset-4 decoration-tns-ivory-dark hover:decoration-tns-gold transition-colors min-h-[44px] px-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tns-gold/50 disabled:opacity-50" data-id="element-939" data-mp="38klgvmqo6glt">
            Back
          </button>
        </div>
      </form>
    </>;
};